import axios from "axios";
import React, { useState, useEffect, useMemo } from "react";
import { Edit } from "@mui/icons-material";
import {
  Container,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Grid,
  Stack,
  Paper,
} from "@mui/material";

import CibilDialog from "./CibilDialog";
import { endpoints } from "../../../api/endpoint";
import { useCities } from "../../../hooks/useCities";
import { formatAmount } from "../../utils/formatAmount";
import { useEmiAdminConfig } from "../../../hooks/useEmiAdminConfig";
import { useFinancerEligibility } from "../../../hooks/useFinancerEligibility";
import { CalculatorIcon, Info } from "lucide-react";
import WrapperComponent from "../WrapperComponent";
import PrimaryButtom from "../../../components/buttons/PrimaryButton";
import { NextButton } from "../../../components/buttons";
import EmiBreakdown from "./EmiBreakdown";

export const BRAND_COLORS = {
  primary: "#1DA7A7",
  dark: "#0F7A6D",
  light: "#E0F7F5",
  accent: "#00BFA5",
  text_dark: "#1a1a1a",
  text_light: "#666666",
  bg_light: "#F8FFFE",
  border_color: "#E5F2F0",
};

export default function EmiCalculation({
  formData,
  setFormData,
  emiResult,
  setEmiResult,
  isCibilDialogOpen,
  setIsCibilDialogOpen,
  setCibilScoreInput,
  cibilScoreInput,
  setActiveStep,
}) {
  const {
    data: emiAdminConfig,
    isLoading: isConfigLoading,
    error: configError,
  } = useEmiAdminConfig();

  const { data: cities, isLoading: isCities } = useCities();

  const [cibilError, setCibilError] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const eligibleFinancers = useFinancerEligibility(
    emiAdminConfig?.financiers,
    formData,
  );

  // Deduplicate by displayName, preserving one internal key per display name
  const uniqueFinancers = useMemo(() => {
    const seen = new Map();
    eligibleFinancers.forEach((key) => {
      const displayName = emiAdminConfig.financiers[key]?.displayName ?? key;
      if (!seen.has(displayName)) seen.set(displayName, key);
    });
    // return [{ key, displayName }, ...] instead of just displayNames
    return Array.from(seen.entries()).map(([displayName, key]) => ({
      key,
      displayName,
    }));
  }, [eligibleFinancers, emiAdminConfig?.financiers]);

  const uniqueCities = cities
    ? Array.from(new Set(cities.filter((c) => !c.startsWith("TEST"))))
    : [];

  useEffect(() => {
    if (isConfigLoading) return;
    if (
      formData.financer &&
      !uniqueFinancers.some((f) => f.key === formData.financer)
    ) {
      setFormData((prev) => ({ ...prev, financer: "" }));
    }
  }, [
    uniqueFinancers.map((f) => f.key).join(","),
    formData.financer,
    setFormData,
  ]);

  const handleCibilSubmit = () => {
    setCibilError("");

    if (!cibilScoreInput) {
      setCibilError("CIBIL score is required");
      return;
    }

    const score = parseInt(cibilScoreInput);
    const minCIBIL = emiAdminConfig?.minCIBIL || 500;

    if (score < minCIBIL) {
      setCibilError(`Minimum required CIBIL score is ${minCIBIL}`);
      return;
    }
    if (score !== formData.cibilScore) {
      setFormData({
        cibilScore: score,
        city: "",
        batteryType: "",
        downpayment: "",
        tenure: "",
        financer: "",
      });
    }

    setCibilScoreInput(score);
    setIsCibilDialogOpen(false);
  };

  // DP options from global config range — financier not yet selected at this point
  const getDownpaymentOptions = () => {
    if (!emiAdminConfig?.downPayment || !formData.city) return [];
    const { min, max, step } = emiAdminConfig.downPayment;
    const options = [];
    for (let i = min; i <= max; i += step) {
      options.push(i);
    }
    return options;
  };

  const calculateEMI = async () => {
    setErrors({});
    setApiError("");

    if (
      !formData.city ||
      !formData.batteryType ||
      !formData.downpayment ||
      !formData.tenure ||
      !formData.financer
    ) {
      setErrors({ general: "Please fill all required fields" });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        downPayment: parseInt(formData.downpayment),
        tenure: parseInt(formData.tenure),
        cibilScore: parseInt(formData.cibilScore),
        financier: formData.financer,
        city: formData.city,
        batteryType: formData.batteryType,
      };

      const response = await axios.post(endpoints.loanEmiCalculation, payload);
      const data = response.data;
      if (!data) throw new Error("Invalid response");
      setEmiResult(response.data);
    } catch (error) {
      setApiError(
        error.response?.data?.message ||
          error.message ||
          "EMI calculation failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditCibil = () => setIsCibilDialogOpen(true);

  const handleReset = () => {
    setFormData((prev) => ({
      ...prev,
      city: "",
      batteryType: "",
      downpayment: "",
      tenure: "",
      financer: "",
    }));
    setErrors({});
    setApiError("");
    setEmiResult(null);
    setActiveStep(1);
  };

  const downpaymentOptions = getDownpaymentOptions();
  const isResponse = Boolean(emiResult);
  const isSubmitDisabled = loading || isResponse;

  if (configError) {
    return <Alert severity="error">Failed to load config</Alert>;
  }

  return (
    <WrapperComponent
      title="EMI Calculator"
      subTitle="Calculate your battery loan EMI with precision"
      icon={<CalculatorIcon size={40} color="white" />}
      actions={
        isResponse
          ? [
              <PrimaryButtom
                background="black"
                text="Calculate Another"
                onClick={handleReset}
                sx={{ ":hover": { opacity: 0.7 } }}
              />,
              <NextButton onClick={() => setActiveStep((prev) => prev + 1)} />,
            ]
          : []
      }
    >
      {isConfigLoading ? (
        <Box
          sx={{
            minHeight: "50vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container sx={{ gap: 4 }}>
          <Grid item xs={12}>
            <Paper
              elevation={8}
              sx={{
                p: 4,
                borderRadius: "16px",
                boxShadow: "none",
                borderTop: `4px solid ${BRAND_COLORS.primary}`,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}
              >
                <Info style={{ fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Loan Details
                </Typography>
              </Box>

              {errors.general && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {errors.general}
                </Alert>
              )}

              {apiError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {apiError}
                </Alert>
              )}

              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  background: `linear-gradient(135deg, ${BRAND_COLORS.light}, #E0F8F5)`,
                  border: `2px solid ${BRAND_COLORS.primary}`,
                  borderRadius: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: BRAND_COLORS.text_light,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Your CIBIL Score
                  </Typography>
                </Box>
                {formData.cibilScore && (
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <Chip
                      label={formData.cibilScore}
                      sx={{
                        bgcolor: BRAND_COLORS.primary,
                        color: "white",
                        fontWeight: 700,
                        fontSize: "1rem",
                        height: "auto",
                        px: 1,
                        py: 0.5,
                      }}
                    />
                    <IconButton
                      disabled={isSubmitDisabled}
                      size="small"
                      onClick={handleEditCibil}
                      title="Edit CIBIL Score"
                      sx={{
                        color: BRAND_COLORS.primary,
                        "&:hover": { bgcolor: `${BRAND_COLORS.primary}15` },
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

              <Stack direction={{ xs: "column", md: "row" }} spacing={3} mb={3}>
                <Box flex={1}>
                  <FormControl fullWidth>
                    <InputLabel>Select Tenure</InputLabel>
                    <Select
                      disabled={isSubmitDisabled}
                      value={formData.tenure}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          tenure: e.target.value,
                        }))
                      }
                      label="Select Tenure"
                    >
                      {emiAdminConfig?.tenures?.map((tenure) => (
                        <MenuItem key={tenure} value={tenure}>
                          {tenure} months
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box flex={1}>
                  <FormControl fullWidth>
                    <InputLabel>Select Battery</InputLabel>
                    <Select
                      disabled={isSubmitDisabled}
                      value={formData.batteryType}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          batteryType: e.target.value,
                        }))
                      }
                      label="Select Battery"
                    >
                      {emiAdminConfig?.batteryTypes?.map((battery) => (
                        <MenuItem key={battery} value={battery}>
                          {battery}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={3} mb={3}>
                <Box flex={1}>
                  <FormControl fullWidth>
                    <InputLabel>Select City</InputLabel>
                    <Select
                      disabled={isSubmitDisabled}
                      value={formData.city}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          city: e.target.value,
                          financer: "",
                          downpayment: "",
                        }))
                      }
                      label="Select City"
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                      {isCities ? (
                        <MenuItem disabled key="loading" value="">
                          Loading...
                        </MenuItem>
                      ) : (
                        uniqueCities?.map((city) => (
                          <MenuItem key={city} value={city}>
                            {city}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Box>

                <Box flex={1}>
                  <FormControl fullWidth>
                    <InputLabel>Select Down Payment</InputLabel>
                    <Select
                      disabled={isSubmitDisabled || !formData.city}
                      value={formData.downpayment}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          downpayment: e.target.value,
                          financer: "",
                        }))
                      }
                      label="Select Down Payment"
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                      {!formData.city && (
                        <MenuItem value="" disabled>
                          <em>Select a city first</em>
                        </MenuItem>
                      )}
                      {formData.city &&
                        downpaymentOptions.map((dp) => (
                          <MenuItem key={dp} value={dp}>
                            {formatAmount(dp)}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              </Stack>

              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Select Financier</InputLabel>
                  <Select
                    disabled={isSubmitDisabled || !formData.downpayment}
                    value={formData.financer}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        financer: e.target.value,
                      }))
                    }
                    label="Select Financier"
                  >
                    {(!formData.city || !formData.downpayment) && (
                      <MenuItem value="" disabled>
                        <em>Select a city & down payment first</em>
                      </MenuItem>
                    )}
                    {formData.city &&
                      formData.downpayment &&
                      uniqueFinancers.map(({ key, displayName }) => (
                        <MenuItem key={key} value={key}>
                          {displayName}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                {formData.city &&
                  formData.downpayment &&
                  uniqueFinancers.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      No financiers available
                    </Alert>
                  )}
              </Box>

              {!isResponse && (
                <PrimaryButtom
                  onClick={calculateEMI}
                  disabled={loading}
                  text={loading ? "Calculating..." : "Calculate EMI"}
                  startIcon={
                    loading ? (
                      <CircularProgress
                        size={24}
                        sx={{ mr: 1, color: "white" }}
                      />
                    ) : null
                  }
                />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            {isResponse && <EmiBreakdown emiResult={emiResult} />}
          </Grid>
        </Grid>
      )}

      <CibilDialog
        open={isCibilDialogOpen && !isConfigLoading}
        cibilScoreInput={cibilScoreInput}
        setCibilScoreInput={setCibilScoreInput}
        cibilError={cibilError}
        setCibilError={setCibilError}
        minCIBIL={emiAdminConfig?.minCIBIL}
        onSubmit={handleCibilSubmit}
      />
    </WrapperComponent>
  );
}
